import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

if (!supabaseUrl || !supabaseAnonKey || !supabaseServiceRoleKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Create a single instance of the Supabase client
let supabaseInstance: ReturnType<typeof createClient> | null = null;
let adminSupabaseInstance: ReturnType<typeof createClient> | null = null;

// Initialize Supabase client only once
export const getSupabaseClient = () => {
  if (!supabaseInstance) {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'snapspace-auth',
        flowType: 'pkce'
      },
      realtime: {
        timeout: 10000, // 10 seconds
        headers: {
          'X-Client-Info': 'snapspace-web-realtime',
        },
        params: {
          eventsPerSecond: 20,
          reconnect: true,
          maxRetries: 5,
          retryDelay: 2000,
          timeout: 10000
        },
      },
      global: {
        headers: {
          'X-Client-Info': 'snapspace-web',
          'Apikey': supabaseAnonKey,
          'Content-Type': 'application/json'
        }
      }
    });
    
    // Set up real-time connection listeners
    const channel = supabaseInstance.channel('connection-status');
    
    channel
      .on('system', {}, (event) => {
        console.log('Realtime system event:', event);
        if (event.event === 'CHANNEL_ERROR') {
          console.error('Realtime channel error:', event);
        }
      })
      .on('broadcast', { event: 'connection' }, (payload) => {
        console.log('Realtime connection status:', payload);
      })
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });
  }
  return supabaseInstance;
};

// Initialize Admin Supabase client only when needed
export const getAdminSupabaseClient = () => {
  if (!adminSupabaseInstance) {
    adminSupabaseInstance = createClient(
      supabaseUrl,
      supabaseServiceRoleKey,
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false
        },
        global: {
          headers: {
            'Authorization': `Bearer ${supabaseServiceRoleKey}`
          }
        }
      }
    );
  }
  return adminSupabaseInstance;
};

export const supabase = getSupabaseClient();
export const adminSupabase = getAdminSupabaseClient();

// Test realtime subscription (opt-in)
const testRealtime = () => {
  console.log('Setting up test realtime subscription...');
  
  const channel = supabase.channel('test-connection', {
    config: {
      presence: {
        key: 'test-connection',
      },
    },
  });
  
  channel
    .on('presence', { event: 'sync' }, () => {
      console.log('✅ Realtime sync established');
      channel.track({ online_at: new Date().toISOString() });
    })
    .subscribe((status) => {
      console.log('Test subscription status:', status);
    });
  
  return () => {
    console.log('Cleaning up test subscription...');
    channel.unsubscribe();
  };
};

// Simple test function (opt-in)
export const testConnection = async () => {
  try {
    console.log('Testing Supabase connection...');
    
    // Test database connection
    const { data, error } = await supabase
      .from('rooms')
      .select('*')
      .limit(1);
    
    if (error) {
      console.error('Supabase error:', error);
      return { success: false, error };
    }
    
    console.log('✅ Successfully connected to Supabase!');
    console.log('Sample room data:', data);
    
    // Only test realtime if in development
    if (import.meta.env.DEV) {
      const cleanup = testRealtime();
      // Cleanup after 5 seconds
      setTimeout(() => {
        cleanup();
      }, 5000);
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('Error testing connection:', error);
    return { success: false, error };
  }
};

// Don't automatically run tests on import
// Call testConnection() manually when needed